import { z } from "zod";

import { menuHandleSchema } from "../menu/menu";

/**
 * Field-level vocabulary for the Header & Footer Builder
 * (feature: header-footer-builder) — the value types every element's props are
 * built from, and the shared prop shapes the two builders have in common.
 *
 * Kept apart from `elements.ts` because these rules are the ones with a reason
 * behind them: which link fields accept a relative path, why every text field
 * tolerates empty, how a phone field is stripped. `elements.ts` is the catalog
 * that assembles them; this is where the reasoning lives.
 */

export const uidSchema = z.string().min(1).max(64);

/** The document discriminator: the URL segment, the `agency_header_footer.kind`
 *  column, and the prefix that keeps rendered DOM ids unique (see `dom-ids.ts`). */
export const headerFooterKindEnum = z.enum(["header", "footer"]);
export type HeaderFooterKind = z.infer<typeof headerFooterKindEnum>;

// ── Shared primitives ───────────────────────────────────────────────────────

/**
 * Every text, link, and email field in both builders is OPTIONAL, and its
 * format is checked only once something has been typed. That is the
 * prototypes' one and only field validator — `ok = !v || RE.test(v)`
 * (header:1488) — and neither prototype gates Apply on a filled-in field at
 * all.
 *
 * This is load-bearing, not an oversight to tighten up: the layout seeds
 * depend on it. `layout-2` seeds an Icon Text with an empty body, `layout-4`
 * seeds Stats with no figures, and Social seeds three platforms with empty
 * URLs — a `.min(1)` on any of those makes a freshly seeded document fail its
 * own schema before the operator has typed a character. AC-04 names exactly
 * three rejection causes (a structural element missing, a single-instance
 * element duplicated, a position illegal under the layout); an unfilled field
 * is not one of them.
 *
 * An element with nothing filled in renders as nothing, the same way it
 * previews as nothing.
 */
export const optionalText = (max: number) => z.string().trim().max(max);

/** Copied from the prototypes' three regexes (header:1483-1485), except the
 *  site-path branch of `FLEX_URL_RE` — see `isSitePath`. */
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
const HTTPS_URL_RE = /^https:\/\/[^\s/$.?#][^\s]*$/i;
const FLEX_URL_RE = /^(https?:\/\/[^\s/$.?#][^\s]*|\/(?![/\\])[^\s]*)$/i;

/**
 * A path on the operator's own storefront: one leading `/`, and never a
 * second `/` or a `\` after it. A browser reads `//host/x` (and `/\host`,
 * which it normalises to the same thing) as "same scheme, OTHER host", so the
 * prototypes' `\/[^\s]*` branch let an off-site address pass as a site path
 * and the storefront then client-navigated and prefetched it as internal.
 * The schema's path branch above and the storefront's `HfAnchor` both use
 * this one rule (QA phase 2, X2).
 */
export function isSitePath(href: string): boolean {
  return /^\/(?![/\\])/.test(href);
}

/**
 * A picture picked from the agency's own media library. `fileId` is validated
 * for tenant ownership + public visibility at the application layer, not
 * here — a Zod parse has no database to check it against (mirrors
 * `UpdateAgencyLogoService`'s `assertUsableAsLogo`).
 *
 * `url` is the `src` the storefront actually renders, and it is client-supplied
 * alongside `fileId` rather than re-derived from it. `MEDIA_URL_RE` is what
 * bounds it: `z.string().url()` alone accepts `javascript:` and `data:` (both
 * inert in `<img>`, but neither is a media URL) and carries no length cap while
 * every other string in this file does. The scheme stays `http(s)` rather than
 * `https`-only because `CDN_PUBLIC_BASE_URL` is env-driven and plain `http`
 * locally.
 *
 * What this does NOT do is pin the host — the CDN origin lives in
 * operator-api's files module. `ApplyHeaderFooterService` pins it on Apply
 * (`isIssuedUrlForFile`): `url` must be an address the files module itself
 * issues for the checked `fileId`, so a document cannot pass AC-05's file
 * check while `url` points at a third party.
 */
const MEDIA_URL_RE = /^https?:\/\/[^\s/$.?#][^\s]*$/i;

/** Limits the builder warns at before Apply would reject. */
export const IMAGE_ALT_MAX_CHARS = 200;
export const STATS_MAX_FIGURES = 10;
export const STATS_VALUE_MAX_CHARS = 20;
export const STATS_LABEL_MAX_CHARS = 40;

export const imageRefSchema = z
  .object({
    fileId: z.string().uuid(),
    url: z
      .string()
      .max(2048)
      .regex(MEDIA_URL_RE, "Enter a full image URL, starting with http(s)://"),
    alt: z.string().max(IMAGE_ALT_MAX_CHARS),
  })
  .strict();
export type ImageRef = z.infer<typeof imageRefSchema>;

/** The header's Button — the primary conversion action, and the one link field
 *  held to `https://` only (`validate: 'httpsurl'`, header:1126). */
export const httpsLinkSchema = optionalText(2048).refine(
  (v) => v === "" || HTTPS_URL_RE.test(v),
  { message: "Enter a full link, starting with https://" },
);

/**
 * Every other link field: Icon Text's link, the footer's Button and CTA, and
 * each Social URL. May point off-site or at another page on the same
 * storefront (`/…`).
 *
 * The prototypes disagree here and this takes the permissive side on purpose:
 * the header's `URL_RE` accepts a leading `/` (header:1484) and the footer's
 * does not (footer:2540). Rejecting an operator's valid relative link is the
 * worse of the two failures, and nothing in the design asks for the footer to
 * be stricter.
 */
export const flexLinkSchema = optionalText(2048).refine(
  (v) => v === "" || FLEX_URL_RE.test(v),
  { message: "Enter a full link, starting with https://, http://, or /" },
);

const TEL_RE = /^tel:\+?[\d\-\s().]{3,30}$/i;
const MAILTO_RE = /^mailto:[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/i;

/** Icon Text's link: everything `flexLinkSchema` takes, plus `tel:` and
 *  `mailto:`, so a "Call us" or "Email us" line can be tapped (TC-66 #27). */
export const CONTACT_LINK_MESSAGE =
  "Enter a full link (https://…), a site path starting with /, tel: or mailto:";

export const contactLinkSchema = optionalText(2048).refine(
  (v) => v === "" || FLEX_URL_RE.test(v) || TEL_RE.test(v) || MAILTO_RE.test(v),
  { message: CONTACT_LINK_MESSAGE },
);

export const emailFieldSchema = optionalText(254).refine(
  (v) => v === "" || EMAIL_RE.test(v),
  { message: "Enter a valid email address" },
);

/** Header's Phone Number field — typed input is stripped to this set live. */
export const phoneDigitsSchema = optionalText(30).regex(/^[\d+-]*$/, {
  message: "Only digits, + and - are allowed",
});

/** Footer's Contact phone list. Also allows spaces, which older saved numbers
 *  have; `+` came in with the country-code picker. */
export const phoneDigitsSpaceSchema = optionalText(30).regex(/^[\d+\-\s]*$/, {
  message: "Only digits, +, hyphens and spaces are allowed",
});

export const hexColorSchema = z
  .string()
  .regex(/^#[0-9a-fA-F]{6}$/, "Enter a hex color, e.g. #0f172a");

/** 22 platforms, both builders — the option set is identical in each prototype. */
export const socialPlatformEnum = z.enum([
  "facebook",
  "instagram",
  "tiktok",
  "youtube",
  "x",
  "linkedin",
  "pinterest",
  "whatsapp",
  "messenger",
  "threads",
  "snapchat",
  "telegram",
  "vimeo",
  "twitch",
  "reddit",
  "tumblr",
  "discord",
  "wechat",
  "weibo",
  "line",
  "viber",
  "other",
]);

/** Always `nofollow` + new tab on render — fixed behavior, not a per-link
 *  setting, so this schema carries nothing for either. */
const socialItemSchema = z
  .object({
    platform: socialPlatformEnum,
    url: flexLinkSchema,
  })
  .strict();

/** 14 marks, footer's Payment Methods only. Nothing checks them against the
 *  gateways enabled in Settings › Payments — the operator is trusted to show
 *  only what they accept, which is why the default is none. */
export const paymentMethodEnum = z.enum([
  "visa",
  "mastercard",
  "amex",
  "discover",
  "dinersclub",
  "jcb",
  "unionpay",
  "maestro",
  "paypal",
  "applepay",
  "googlepay",
  "stripe",
  "klarna",
  "banktransfer",
]);

const ratingStyleEnum = z.enum([
  "tripadvisor",
  "google",
  "trustpilot",
  "facebook",
  "none",
]);
const ratingDisplayEnum = z.enum(["circles", "stars"]);
const iconOptionEnum = z.enum([
  "none",
  "shield",
  "clock",
  "phone",
  "info",
  "pin",
]);
/**
 * Which menu an element points at (feature: header-footer-builder).
 *
 * A Menu Builder HANDLE — the permanent pointer `menuHandleSchema` documents —
 * or `""` for "the agency's first menu" (oldest first). It is a handle and
 * not a menu id because a handle is what every other surface already points at,
 * and it is not a name because a rename must not silently re-point the header.
 *
 * `""` is the default rather than a specific handle for the reason the registry
 * defaults exist at all: an element is seeded before anything has read the
 * agency's menus, so there is no handle to seed it with. It resolves to the
 * first menu, and to the standard navigation when there is none — the same
 * two-step fallback `getMenu` already holds (AC-03).
 *
 * The handle is NOT validated against the agency's menus here. A menu can be
 * deleted after a header is applied, and the storefront answers that by falling
 * back rather than by refusing to render, so a stored handle that no longer
 * resolves is a normal state and not a contract violation.
 */
export const menuRefSchema = z.union([z.literal(""), menuHandleSchema]);

// ── Element props shared by both builders ────────────────────────────────────

/**
 * Seven element types have byte-identical prop shapes in the two prototypes'
 * catalogs — only their seeded default VALUES differ (Ratings' label reads
 * "188 reviews" in the header and "120 reviews" in the footer; Stats seeds
 * empty in the header and two figures in the footer). They are declared once
 * here and spread into both unions, so the pair cannot drift apart later.
 *
 * `button` is deliberately NOT in this set: the header's has `showMobile` and
 * the footer's has no mobile bar to opt into.
 *
 * Sharing the shape is sharing the CSS class too, never the DOM id — a header
 * Social block and a footer Social block are the same component on the same
 * page. See `dom-ids.ts`.
 */
export const SHARED_ELEMENT_PROPS = {
  image: {
    heading: optionalText(80),
    img: imageRefSchema.nullable(),
    /** Rendered as `p.width || 96` (header:1833, footer:2009). Neither
     *  prototype ships an editor for it, so no range was ever stated — left
     *  open rather than inventing a bound the design never set. */
    width: z.number().int().positive(),
  },
  ratings: {
    style: ratingStyleEnum,
    display: ratingDisplayEnum,
    /** `null` is "not set yet". A palette default and every layout seed start
     *  there, so a never-edited Ratings element renders as nothing rather
     *  than publishing a score nobody entered. */
    rating: z.number().min(1).max(5).multipleOf(0.1).nullable(),
    label: optionalText(100),
  },
  /** "Icon Text" in the dashboard. The prototypes key this `text` and key the
   *  plain paragraph `about` — the reverse of what the names suggest, kept as
   *  written so the renderer port stays a literal read. */
  text: {
    text: optionalText(500),
    icon: iconOptionEnum,
    href: contactLinkSchema,
    newTab: z.boolean(),
  },
  /** "Text" in the dashboard — the plain paragraph. */
  about: {
    text: optionalText(500),
  },
  stats: {
    figures: z
      .array(
        z
          .object({
            value: optionalText(STATS_VALUE_MAX_CHARS),
            label: optionalText(STATS_LABEL_MAX_CHARS),
          })
          .strict(),
      )
      .max(STATS_MAX_FIGURES),
  },
  social: {
    profiles: z.array(socialItemSchema).max(22),
  },
  /** Read-only in both builders — what travelers can pick comes from the
   *  agency's language and currency settings, so there is nothing to store. */
  langcur: {},
} as const;
